import { useMemo } from 'react';
import { Alert, Button, Divider, Stack, Typography } from '@mui/material';
import { ArrowBack, ListAlt, OpenInNew } from '@mui/icons-material';
import { useFormContext, useFormState, useWatch } from 'FrontRoyalReactHookForm';
import { TextField } from 'FrontRoyalMaterialUiForm';
import { RenderMarkdown } from 'MarkdownComponents';
import { lessonEditorPath, streamEditorPath } from 'Lessons';
import {
    EditContentItemLocale,
    EntityMetadataForm,
    SavePublishPanel,
    SavingOverlay,
    SectionHeading,
    TranslatableExportSetStatus,
} from 'ContentItemEditor';
import ChaptersSection from './ChaptersSection';
import ContentTopicsField from './ContentTopicsField';
import CourseHighlightsSection from './CourseHighlightsSection';
import CoursePickerSection from './CoursePickerSection';
import MetadataCsvButtons from './MetadataCsvButtons';
import ResourceDownloadsSection from './ResourceDownloadsSection';
import ResourceLinksSection from './ResourceLinksSection';
import EditorCard from './EditorCard';
import StreamHeaderFields from './StreamHeaderFields';
import SummariesSection from './SummariesSection';
import { useUnsavedChangesWarning } from './useUnsavedChangesWarning';
import { validationMessages } from './validationMessages';
import { type EmbeddedLesson, type StreamEditorFormProps, type StreamFormValues } from './StreamEditor.types';

// oxlint-disable-next-line max-lines-per-function -- the page is one long form of independent panels
export default function StreamEditorForm(props: StreamEditorFormProps) {
    const {
        record,
        recordDirty,
        courses,
        coursesById,
        coursesLoading,
        lessonsById,
        availableLessons,
        lessonsLoaded,
        canPublish,
        saving,
        justSaved,
        deleting,
        uploadingImage,
        uploadingFor,
        exportSet,
        publishState,
        onPublishStateChange,
        onSave,
        onDelete,
        onSaveMetadata,
        savingMetadata,
        onRecordEdit,
        onUploadImage,
        onUploadSeoImage,
        onUploadDownloads,
        onUploadSummaries,
        onReviewKeyTerms,
        onDuplicate,
        onExportAllLessons,
        onGoBack,
    } = props;

    const { control, watch, setFieldValue } = useFormContext<StreamFormValues>();
    const { isDirty, isValid, errors } = useFormState({ control });

    const title = watch('title');
    const locale = watch('locale');
    const credits = watch('credits');

    // The form cannot see an image or a locale pack change, so both are folded in here. Without
    // that, leaving the page after only an image change would not warn.
    useUnsavedChangesWarning(isDirty || recordDirty);

    const assessmentEditingLocked = !!record.locale_pack?.assessment_editing_locked;

    /*
        The lessons a summary may cover, taken from the chapters so a lesson added in this session is
        offered at once. record.lessons is the list from load and never changes: the save leaves it
        alone on purpose, because the response carries only three fields. useWatch, not watch: see
        ChaptersSection.
    */
    const chapters = useWatch({ control, name: 'chapters' });
    const streamLessons = useMemo(() => {
        const lessonIds = (chapters || []).flatMap(chapter =>
            (chapter.lesson_hashes || []).map(lessonHash => lessonHash.lesson_id),
        );

        return [...new Set(lessonIds)]
            .map(lessonId => lessonsById[lessonId])
            .filter((lesson): lesson is EmbeddedLesson => !!lesson);
    }, [chapters, lessonsById]);

    const linkableCourses = useMemo(
        () =>
            courses.map(course => ({
                id: course.id,
                title: course.title,
                tag: course.tag,
                locale: course.locale || 'en',
                localePackId: course.localePackId,
            })),
        [courses],
    );

    return (
        <Stack spacing={4} sx={{ paddingBottom: 6 }}>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Button startIcon={<ArrowBack />} onClick={onGoBack}>
                    Back to Courses List
                </Button>

                {!!record.published_at && (
                    <Button
                        component="a"
                        href={`/course/${record.id}`}
                        target="_blank"
                        rel="noreferrer"
                        variant="contained"
                        color="primary"
                        endIcon={<OpenInNew />}
                    >
                        Open Course Page
                    </Button>
                )}
            </Stack>

            <EditorCard title="Course info">
                <StreamHeaderFields
                    streamId={record.id}
                    image={record.image}
                    localePackId={record.locale_pack?.id}
                    assessmentEditingLocked={assessmentEditingLocked}
                    uploadingImage={uploadingImage}
                    onUploadImage={onUploadImage}
                    onRemoveImage={() => onRecordEdit({ image: null })}
                    courses={courses}
                    coursesLoading={coursesLoading}
                />
            </EditorCard>

            {/* Two columns: every card here holds short values, which stretch past any useful
                length at full page width. The left column is the wider, and Chapters is the one
                card whose height follows the content, so the balance is only approximate. */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ alignItems: 'flex-start' }}>
                <Stack spacing={4} sx={{ flex: 1.4, minWidth: 0, width: '100%' }}>
                    <EditorCard title="Chapters">
                        <ChaptersSection
                            lessonsById={lessonsById}
                            availableLessons={availableLessons}
                            lessonsLoaded={lessonsLoaded}
                            assessmentEditingLocked={assessmentEditingLocked}
                            editorUrlForLesson={lessonId => lessonEditorPath({ lessonId })}
                        />

                        <Divider />

                        <Stack spacing={1}>
                            <SectionHeading>Key Terms</SectionHeading>
                            <Typography variant="body2">View all key terms in this course, consolidated.</Typography>
                            <Button
                                name="review_key_terms"
                                variant="outlined"
                                startIcon={<ListAlt />}
                                onClick={onReviewKeyTerms}
                                sx={{ alignSelf: 'flex-start' }}
                            >
                                Review Key Terms
                            </Button>
                        </Stack>
                    </EditorCard>

                    <EditorCard title="Related & Recommended Courses">
                        <CoursePickerSection
                            name="recommended_stream_ids"
                            label="Recommended Study Courses"
                            courses={courses}
                            coursesById={coursesById}
                            locale={locale}
                        />

                        <Divider />

                        <CoursePickerSection
                            name="related_stream_ids"
                            label="Related Courses"
                            courses={courses}
                            coursesById={coursesById}
                            locale={locale}
                        />
                    </EditorCard>

                    <EditorCard title="Additional Resources">
                        <ResourceDownloadsSection
                            uploading={uploadingFor === 'resource_downloads'}
                            onPickFiles={onUploadDownloads}
                        />

                        <Divider />

                        <SummariesSection
                            streamLessons={streamLessons}
                            uploading={uploadingFor === 'summaries'}
                            onPickFiles={onUploadSummaries}
                        />

                        <Divider />

                        <ResourceLinksSection />
                    </EditorCard>
                </Stack>

                <Stack spacing={4} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <EditorCard title="Highlights & Key Topics">
                        <CourseHighlightsSection />

                        <Divider />

                        {locale === 'en' ? (
                            <ContentTopicsField locale={locale} />
                        ) : (
                            <Stack spacing={1}>
                                <SectionHeading>Key Topics Covered</SectionHeading>
                                <Typography variant="body2">
                                    Content Topics selector is only available for the English course
                                </Typography>
                            </Stack>
                        )}
                    </EditorCard>

                    <EditorCard title="Credits">
                        <Stack spacing={1}>
                            {/* Padding, not margin — see EditorCard. */}
                            <Typography variant="body2" sx={{ paddingBottom: 1 }}>
                                Markdown formatting allowed.
                            </Typography>

                            <TextField
                                name="credits"
                                label="Credits"
                                fullWidth
                                multiline
                                rows={4}
                                slotProps={{ htmlInput: { dir: 'auto' } }}
                            />

                            <Stack sx={{ border: '1px dashed', borderColor: 'divider', minHeight: '6em', padding: 1 }}>
                                <RenderMarkdown>{credits || ''}</RenderMarkdown>
                            </Stack>
                        </Stack>
                    </EditorCard>

                    <EditorCard title="Translations and exports">
                        <EditContentItemLocale
                            itemId={record.id}
                            itemTitle={title}
                            locale={locale}
                            localePack={record.locale_pack}
                            linkableItems={linkableCourses}
                            loading={coursesLoading}
                            groupable
                            editorUrlFor={courseId => streamEditorPath({ streamId: courseId })}
                            onChange={({ locale: newLocale, localePack }) => {
                                setFieldValue('locale', newLocale);

                                // The locale goes on the record as well as in the form, because it
                                // keys the lesson list query. Without it the pickers keep offering
                                // the lessons of the locale the page loaded with.
                                onRecordEdit({ locale: newLocale, locale_pack: localePack });
                            }}
                        />

                        <Button
                            variant="outlined"
                            color="warning"
                            onClick={onDuplicate}
                            disabled={saving}
                            sx={{ alignSelf: 'flex-start' }}
                        >
                            Create new Localization
                        </Button>

                        <Divider />

                        <Stack spacing={3}>
                            <Stack spacing={1}>
                                <SectionHeading>Translatable HTML Files</SectionHeading>
                                <Button
                                    name="export-all-lessons"
                                    variant="outlined"
                                    onClick={onExportAllLessons}
                                    disabled={!!exportSet && !exportSet.prepared}
                                    sx={{ alignSelf: 'flex-start' }}
                                >
                                    Export all lessons
                                </Button>
                                <TranslatableExportSetStatus exportSet={exportSet} />
                            </Stack>

                            <MetadataCsvButtons />
                        </Stack>
                    </EditorCard>
                </Stack>
            </Stack>

            <EditorCard title="Save, Publish, & Delete Course">
                {/* Both conditions: isValid turns false before the resolver has filled in errors,
                    and a banner with no reasons under it says less than nothing. */}
                {!isValid && validationMessages(errors).length > 0 && (
                    <Alert severity="warning">
                        <Stack spacing={0.5}>
                            <span>The course cannot be saved yet:</span>

                            {validationMessages(errors).map(message => (
                                <span key={message}>{message}</span>
                            ))}
                        </Stack>
                    </Alert>
                )}

                <SavePublishPanel
                    itemHeader="Course"
                    title={title}
                    modifiedAt={record.modified_at}
                    publishedAt={record.published_at}
                    canPublish={canPublish}
                    busy={saving || deleting}
                    justSaved={justSaved}
                    publishState={publishState}
                    onPublishStateChange={onPublishStateChange}
                    onSave={onSave}
                    onDelete={onDelete}
                    /*
                        Validity only. An unchanged course is still saveable, as it is in the
                        Angular page, where the Save button is disabled by the in-flight guard and
                        nothing else.

                        Publishing depends on it: an admin ticks 'Publish new version when I save'
                        and saves, without editing a field. A dirty check would make the one button
                        that publishes a draft impossible to press.
                    */
                    saveDisabled={!isValid}
                />
            </EditorCard>

            {/* The card goes with its contents. EntityMetadataForm hides itself below admin, which
                left a super editor looking at an empty titled card; the Angular directive carried
                its ng-if on the template root. canPublish is the same admin check the form makes. */}
            {canPublish && (
                <EditorCard title="SEO Fields">
                    <EntityMetadataForm
                        itemId={record.id}
                        urlPrefix="course"
                        image={record.entity_metadata?.image}
                        uploadingImage={uploadingImage}
                        onUploadImage={onUploadSeoImage}
                        onRemoveImage={() =>
                            onRecordEdit({ entity_metadata: { ...record.entity_metadata!, image: null } })
                        }
                        onSave={onSaveMetadata}
                        saving={savingMetadata}
                    />
                </EditorCard>
            )}

            <SavingOverlay open={saving} />
        </Stack>
    );
}
